import { useEffect, useMemo, useState } from 'react';
import { portalApi } from '../api';
import type { PublicPageOut } from '../types';

const JUMP = [
  { id: 'mission', label: 'Миссия и платформа' },
  { id: 'structure', label: 'Структура и ответственные' },
  { id: 'founder', label: 'О создателе', hash: 'creator' },
  { id: 'achievements', label: 'Достижения' },
  { id: 'legal', label: 'Нормативная база' },
  { id: 'partners', label: 'Партнёры' },
];

const FEATURES = [
  {
    title: 'Единый контур',
    text: 'Публичная часть показывает опубликованные представления единых объектов без дублирования файлов и заявок.',
  },
  {
    title: 'Проверяемые источники',
    text: 'Каталожные объекты используют официальный источник, дату проверки и понятный disclaimer.',
  },
  {
    title: 'Ролевая система',
    text: 'Owner публикует напрямую; публикации сообщества проходят модерацию.',
  },
];

const LEGAL = [
  'Политика публичных публикаций',
  'Политика обработки файлов и публичного доступа',
  'Правила модерации и официальных источников',
];

const EMPTY_LIST_MSG = 'Пока нет данных — раздел редактируется владельцем';

type PersonItem = { name: string; role: string; who: string };
type AchievementItem = { year: string; title: string; text: string };

function parsePeople(content: Record<string, unknown> | undefined): PersonItem[] {
  if (!content) return [];
  const raw = content.items ?? content.people;
  if (!Array.isArray(raw)) return [];
  return raw
    .map((item) => {
      if (!item || typeof item !== 'object') return null;
      const row = item as Record<string, unknown>;
      const name = String(row.name ?? '').trim();
      if (!name) return null;
      return {
        name,
        role: String(row.role ?? '').trim(),
        who: String(row.who ?? '').trim(),
      };
    })
    .filter((item): item is PersonItem => item !== null);
}

function parseAchievements(content: Record<string, unknown> | undefined): AchievementItem[] {
  if (!content) return [];
  const raw = content.items;
  if (!Array.isArray(raw)) return [];
  return raw
    .map((item) => {
      if (!item || typeof item !== 'object') return null;
      const row = item as Record<string, unknown>;
      const title = String(row.title ?? '').trim();
      if (!title) return null;
      return {
        year: String(row.year ?? '').trim(),
        title,
        text: String(row.text ?? '').trim(),
      };
    })
    .filter((item): item is AchievementItem => item !== null);
}

function parsePartners(content: Record<string, unknown> | undefined): string[] {
  if (!content) return [];
  const raw = content.items ?? content.partners;
  if (!Array.isArray(raw)) return [];
  return raw
    .map((item) => (typeof item === 'string' ? item.trim() : ''))
    .filter(Boolean);
}

export const AboutSection = ({ focusId }: { focusId?: string }) => {
  const [page, setPage] = useState<PublicPageOut | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    portalApi
      .getPage('about')
      .then((data) => setPage({ slug: 'about', content_blocks: data.content_blocks || [] }))
      .catch(() => setPage(null))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!focusId || loading) return;
    const id = focusId === 'creator' ? 'founder' : focusId;
    document.getElementById(`portal-${id}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [focusId, loading, page]);

  const blocksMap = useMemo(() => {
    const blocks = page?.content_blocks || [];
    return new Map(blocks.map((b) => [b.block_type, b.content]));
  }, [page]);

  const people = useMemo(() => parsePeople(blocksMap.get('people')), [blocksMap]);
  const achievements = useMemo(() => parseAchievements(blocksMap.get('achievements')), [blocksMap]);
  const partners = useMemo(() => parsePartners(blocksMap.get('partners')), [blocksMap]);

  if (loading) {
    return <div className="public-empty">Загрузка...</div>;
  }

  const textFor = (keys: string[], fallback: string) => {
    for (const key of keys) {
      const value = blocksMap.get(key)?.text;
      if (typeof value === 'string' && value.trim()) return value;
    }
    return fallback;
  };

  const mission = textFor(
    ['mission'],
    'ZERTTEU ACADEMY объединяет научные проекты, публикации, обучение, академические связи и публичные возможности в одной цифровой среде.',
  );

  return (
    <div className="portal-section">
      <div className="public-hero portal-hero">
        <div>
          <span className="public-eyebrow portal-hero-kicker">О проекте</span>
          <h2>Zertteu Academy</h2>
          <p>{mission}</p>
        </div>
      </div>

      <nav className="portal-about-jump" aria-label="Разделы О нас">
        {JUMP.map((item) => (
          <a
            key={item.id}
            href={`#${item.hash || item.id}`}
            className={
              focusId === item.id || focusId === item.hash || (focusId === 'creator' && item.id === 'founder')
                ? 'active'
                : undefined
            }
          >
            {item.label}
          </a>
        ))}
      </nav>

      <section className="portal-cms-section" id="portal-mission">
        <header>
          <div>
            <small>01</small>
            <h2>Миссия и платформа</h2>
          </div>
        </header>
        <p className="portal-lead">{mission}</p>
        <div className="portal-feature-grid">
          {FEATURES.map((f) => (
            <article key={f.title}>
              <b>{f.title}</b>
              <p>{f.text}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="portal-cms-section" id="portal-structure">
        <header>
          <div>
            <small>02</small>
            <h2>Структура и ответственные</h2>
          </div>
        </header>
        <p className="portal-lead">
          {textFor(
            ['structure'],
            'Система состоит из функциональных модулей (кабинетов), охватывающих процессы академической работы.',
          )}
        </p>
        {people.length === 0 ? (
          <p className="public-empty">{EMPTY_LIST_MSG}</p>
        ) : (
          <div className="portal-people-grid">
            {people.map((person) => (
              <article key={person.name}>
                <span>{person.name.slice(0, 2).toUpperCase()}</span>
                <div>
                  <h3>{person.name}</h3>
                  <p>{person.role}</p>
                  <small>{person.who}</small>
                </div>
              </article>
            ))}
          </div>
        )}
      </section>

      <section className="portal-cms-section" id="portal-founder">
        <header>
          <div>
            <small>03</small>
            <h2>О создателе</h2>
          </div>
        </header>
        <div className="portal-creator">
          <div className="portal-creator-avatar">АБ</div>
          <div>
            <h3>Создатель ZERTTEU ACADEMY</h3>
            <p>
              {textFor(
                ['founder', 'creator'],
                'Алтын Бакытжанова — основатель проекта и ментор. Публичный профиль показывает только поля, разрешённые владельцем.',
              )}
            </p>
            <div className="portal-tag-row">
              <span>#research</span>
              <span>#academia</span>
              <span>#digital</span>
            </div>
          </div>
        </div>
      </section>

      <section className="portal-cms-section" id="portal-achievements">
        <header>
          <div>
            <small>04</small>
            <h2>Достижения платформы</h2>
          </div>
        </header>
        <p className="portal-lead">
          {textFor(['achievements'], 'Публикации, конкурсы и исследовательские инициативы сообщества.')}
        </p>
        {achievements.length === 0 ? (
          <p className="public-empty">{EMPTY_LIST_MSG}</p>
        ) : (
          <div className="portal-cms-cards">
            {achievements.map((item) => (
              <article key={item.title}>
                <small>{item.year}</small>
                <h3>{item.title}</h3>
                <p>{item.text}</p>
              </article>
            ))}
          </div>
        )}
      </section>

      <section className="portal-cms-section" id="portal-legal">
        <header>
          <div>
            <small>05</small>
            <h2>Нормативная база</h2>
          </div>
        </header>
        <p className="portal-lead">
          {textFor(
            ['legal'],
            'Все данные на портале публикуются в соответствии с законодательством Республики Казахстан.',
          )}
        </p>
        <div className="portal-list-rows">
          {LEGAL.map((doc) => (
            <article key={doc}>
              <b>{doc}</b>
              <span>Актуальная версия</span>
              <button type="button">Открыть документ →</button>
            </article>
          ))}
        </div>
      </section>

      <section className="portal-cms-section" id="portal-partners">
        <header>
          <div>
            <small>06</small>
            <h2>Партнёры и сотрудничество</h2>
          </div>
        </header>
        <p className="portal-lead">
          {textFor(['partners'], 'Сотрудничество с университетами, редакциями и научными организациями.')}
        </p>
        {partners.length === 0 ? (
          <p className="public-empty">{EMPTY_LIST_MSG}</p>
        ) : (
          <div className="portal-partners">
            {partners.map((name) => (
              <article key={name}>
                <strong>{name}</strong>
                <span>Официальный партнёр</span>
              </article>
            ))}
          </div>
        )}
      </section>
    </div>
  );
};
